import { useEffect, useState, useRef, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/lib/jobFields";
import { TagSelect } from "@/components/TagSelect";
import {
  YEARS_EXPERIENCE, SENIORITY_LEVELS, DESIRED_ROLES, SKILLS, COMPANY_STAGES, COMPANY_TYPES,
  COMPENSATION_RANGES, REMOTE_PREFERENCES, RELOCATE_OPTIONS, WORK_AUTH_OPTIONS, TRAVEL_OPTIONS,
  NOTABLE_ACHIEVEMENTS, BOARD_TYPES, LANGUAGES, SOCIAL_LINKS,
} from "@/lib/candidateFields";
import { Loader2, Upload, FileText, Save, Plus, X, Eye, EyeOff, Camera } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-[#F4F4F5] border-black/10 rounded-none";

const Dropdown = ({ label, value, onChange, options, placeholder, testid }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testid}><SelectValue placeholder={placeholder || "Select…"} /></SelectTrigger>
      <SelectContent className="bg-white border-black/10 max-h-64">
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Section = ({ title, hint, children, testid }) => (
  <div className="border border-black/10 bg-white p-6" data-testid={testid}>
    <p className="overline mb-1">{title}</p>
    {hint && <p className="text-xs text-muted-foreground mb-4">{hint}</p>}
    <div className={hint ? "" : "mt-4"}>{children}</div>
  </div>
);

export default function CandidateProfile() {
  const fileRef = useRef();
  const photoRef = useRef();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const load = () => api.get("/me/candidate").then(({ data }) => setC({ ...data, visibility_status: data.visibility_status === "confidential" ? "confidential" : "searchable" }));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const setVal = (k) => (v) => setC({ ...c, [k]: v });
  const setList = (k) => (e) => setC({ ...c, [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) });

  // work experience helpers
  const exp = c?.work_experience || [];
  const addExp = () => setC({ ...c, work_experience: [...exp, { company: "", title: "", start: "", end: "", current: false, impact: "" }] });
  const setExp = (i, k, v) => setC({ ...c, work_experience: exp.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const removeExp = (i) => setC({ ...c, work_experience: exp.filter((_, j) => j !== i) });

  // education helpers
  const edu = c?.education || [];
  const addEdu = () => setC({ ...c, education: [...edu, { school: "", degree: "", year: "" }] });
  const setEdu = (i, k, v) => setC({ ...c, education: edu.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const removeEdu = (i) => setC({ ...c, education: edu.filter((_, j) => j !== i) });

  const social = c?.social_links || {};
  const setSocial = (k, v) => setC({ ...c, social_links: { ...social, [k]: v } });

  const toggleRole = (v) => {
    const cur = c.preferred_role_types || [];
    setC({ ...c, preferred_role_types: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] });
  };

  const completeness = useMemo(() => {
    if (!c) return 0;
    const checks = [
      c.headline, c.location, c.summary, c.compensation_expectations, c.linkedin_url,
      (c.industries || []).length, (c.skills || []).length, c.resume_file_url,
      (c.work_experience || []).length, (c.desired_roles || []).length,
      c.current_company, c.current_title, c.years_experience, c.seniority_level,
      c.remote_preference, c.work_authorization, (c.education || []).length,
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [c]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me/candidate", {
        phone: c.phone || "", headline: c.headline || "", location: c.location || "",
        linkedin_url: c.linkedin_url || "", summary: c.summary || "",
        industries: c.industries || [], functions: c.functions || [],
        compensation_expectations: c.compensation_expectations || "",
        board_interest: !!c.board_interest, fractional_interest: !!c.fractional_interest,
        advisor_interest: !!c.advisor_interest, availability_status: c.availability_status || "open",
        geographic_preferences: c.geographic_preferences || [],
        work_experience: (c.work_experience || []).filter((r) => r.company || r.title),
        preferred_role_types: c.preferred_role_types || [],
        visibility_status: c.visibility_status || "searchable",
        current_company: c.current_company || "", current_title: c.current_title || "",
        years_experience: c.years_experience || "", seniority_level: c.seniority_level || "",
        desired_roles: c.desired_roles || [], skills: c.skills || [],
        preferred_company_stages: c.preferred_company_stages || [],
        preferred_company_types: c.preferred_company_types || [],
        remote_preference: c.remote_preference || "", willing_to_relocate: c.willing_to_relocate || "",
        work_authorization: c.work_authorization || "", travel_willingness: c.travel_willingness || "",
        notable_achievements: c.notable_achievements || [],
        has_board_experience: !!c.has_board_experience,
        board_experience_types: c.has_board_experience ? (c.board_experience_types || []) : [],
        languages: c.languages || [],
        education: (c.education || []).filter((r) => r.school || r.degree),
        portfolio_url: c.portfolio_url || "", social_links: c.social_links || {},
      });
      toast.success("Profile saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/candidate/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/candidate/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Headshot uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingPhoto(false); }
  };

  if (!c) return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const confidential = c.visibility_status === "confidential";
  const bioLen = (c.summary || "").length;

  return (
    <div>
      <PageHeader overline="Your details & resume" title="My Profile">
        <input ref={fileRef} type="file" hidden onChange={upload} accept=".pdf,.doc,.docx,.txt" />
        <Button variant="ghost" onClick={() => fileRef.current.click()} disabled={uploading} data-testid="cand-upload-resume" className="rounded-none text-muted-foreground hover:text-[#09090B]">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Resume
        </Button>
        <Button type="submit" form="cand-profile-form" disabled={saving} data-testid="cand-profile-save" className="rounded-none bg-primary hover:bg-[#09090B]">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
        </Button>
      </PageHeader>

      <form id="cand-profile-form" onSubmit={save} className="p-8 max-w-3xl space-y-6">
        {/* Headshot (optional) */}
        <div className="border border-black/10 bg-white p-5 flex items-center gap-5" data-testid="headshot-section">
          <div className="h-20 w-20 shrink-0 bg-[#F4F4F5] border border-black/10 overflow-hidden flex items-center justify-center">
            {c.photo_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt="Headshot" className="h-full w-full object-cover" data-testid="headshot-preview" />
              : <Camera className="h-7 w-7 text-muted-foreground/50" strokeWidth={1.5} />}
          </div>
          <div className="flex-1">
            <p className="overline text-[0.55rem]">Headshot <span className="text-muted-foreground normal-case tracking-normal font-normal">(optional)</span></p>
            <p className="text-xs text-muted-foreground mt-1">A professional photo helps recruiters put a face to your profile. JPG, PNG or WEBP.</p>
            <input ref={photoRef} type="file" hidden onChange={uploadPhoto} accept=".jpg,.jpeg,.png,.webp" />
            <Button type="button" variant="ghost" onClick={() => photoRef.current.click()} disabled={uploadingPhoto} data-testid="cand-upload-photo" className="rounded-none border border-black/10 text-xs mt-3">
              {uploadingPhoto ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />} {c.photo_url ? "Replace photo" : "Upload photo"}
            </Button>
          </div>
        </div>

        {/* Completeness meter */}
        <div className="border border-black/10 bg-white p-5" data-testid="completeness-meter">
          <div className="flex items-center justify-between mb-2">
            <p className="overline text-[0.55rem]">Profile strength</p>
            <p className="font-heading font-black text-sm">{completeness}%</p>
          </div>
          <div className="h-2 bg-black/10 w-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${completeness}%` }} />
          </div>
          {completeness < 100 && <p className="text-xs text-muted-foreground mt-2">A richer profile ranks stronger with recruiters — add your current role, skills, desired roles, education and a resume.</p>}
        </div>

        {/* Visibility */}
        <div className="border border-black/10 bg-white p-5" data-testid="visibility-toggle">
          <p className="overline text-[0.55rem] mb-3">Visibility</p>
          <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10">
            <button type="button" data-testid="vis-searchable" onClick={() => setC({ ...c, visibility_status: "searchable" })}
              className={`p-4 text-left transition-colors ${!confidential ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
              <span className="flex items-center gap-2 font-heading font-bold text-sm"><Eye className="h-4 w-4" /> Searchable</span>
              <span className={`text-xs mt-1 block ${!confidential ? "text-white/60" : "text-muted-foreground"}`}>Recruiters can discover you and share you with hiring teams.</span>
            </button>
            <button type="button" data-testid="vis-confidential" onClick={() => setC({ ...c, visibility_status: "confidential" })}
              className={`p-4 text-left transition-colors ${confidential ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
              <span className="flex items-center gap-2 font-heading font-bold text-sm"><EyeOff className="h-4 w-4" /> Confidential</span>
              <span className={`text-xs mt-1 block ${confidential ? "text-white/60" : "text-muted-foreground"}`}>Stay listed for recruiters, but they're warned before sharing you with any client.</span>
            </button>
          </div>
        </div>

        {/* Current role & basics */}
        <div className="border border-black/10 bg-white p-6 grid sm:grid-cols-2 gap-4" data-testid="basics-section">
          <div className="space-y-2"><Label>Current company</Label><Input value={c.current_company || ""} onChange={set("current_company")} className={inputCls} placeholder="e.g. Nexus Louisiana" data-testid="cand-current-company" /></div>
          <div className="space-y-2"><Label>Current title</Label><Input value={c.current_title || ""} onChange={set("current_title")} className={inputCls} placeholder="e.g. Director of Ecosystem Development" data-testid="cand-current-title" /></div>
          <Dropdown label="Years of experience" value={c.years_experience} onChange={setVal("years_experience")} options={YEARS_EXPERIENCE} placeholder="Select range" testid="cand-years-exp" />
          <Dropdown label="Seniority level" value={c.seniority_level} onChange={setVal("seniority_level")} options={SENIORITY_LEVELS} placeholder="Select level" testid="cand-seniority" />
          <div className="space-y-2"><Label>Headline</Label><Input value={c.headline || ""} onChange={set("headline")} className={inputCls} placeholder="Ex-CTO, Series B fintech" /></div>
          <div className="space-y-2"><Label>Location</Label><Input value={c.location || ""} onChange={set("location")} className={inputCls} /></div>
          <div className="space-y-2"><Label>Phone</Label><Input value={c.phone || ""} onChange={set("phone")} className={inputCls} /></div>
          <div className="space-y-2"><Label>LinkedIn URL</Label><Input value={c.linkedin_url || ""} onChange={set("linkedin_url")} className={inputCls} placeholder="https://linkedin.com/in/" /></div>
          <Dropdown label="Compensation expectations" value={c.compensation_expectations} onChange={setVal("compensation_expectations")} options={COMPENSATION_RANGES} placeholder="Select range (optional)" testid="cand-compensation" />
          <div className="space-y-2">
            <Label>Availability</Label>
            <Select value={c.availability_status || "open"} onValueChange={(v) => setC({ ...c, availability_status: v })}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-black/10">
                <SelectItem value="open">Open</SelectItem><SelectItem value="passive">Passive</SelectItem><SelectItem value="not_looking">Not looking</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Dropdown label="Remote preference" value={c.remote_preference} onChange={setVal("remote_preference")} options={REMOTE_PREFERENCES} testid="cand-remote-pref" />
          <Dropdown label="Willing to relocate" value={c.willing_to_relocate} onChange={setVal("willing_to_relocate")} options={RELOCATE_OPTIONS} testid="cand-relocate" />
          <Dropdown label="Work authorization" value={c.work_authorization} onChange={setVal("work_authorization")} options={WORK_AUTH_OPTIONS} testid="cand-work-auth" />
          <Dropdown label="Travel willingness" value={c.travel_willingness} onChange={setVal("travel_willingness")} options={TRAVEL_OPTIONS} testid="cand-travel" />
        </div>

        {/* Professional bio */}
        <Section title="Short professional bio" hint="Think LinkedIn “About” — aim for 500–1,000 characters." testid="bio-section">
          <Textarea value={c.summary || ""} onChange={set("summary")} maxLength={1000} className={`${inputCls} min-h-[120px]`} data-testid="cand-bio" placeholder="Your story, focus areas and what you're looking for next…" />
          <p className="text-xs text-muted-foreground mt-1 text-right">{bioLen}/1000</p>
        </Section>

        {/* Desired roles */}
        <Section title="Desired roles" hint="The kinds of roles you want next — pick from the suggestions or type your own and press Enter. Sharpens your matches." testid="desired-roles-section">
          <TagSelect label="" values={c.desired_roles || []} onChange={setVal("desired_roles")} options={DESIRED_ROLES} testid="cand-desired-roles" />
        </Section>

        {/* Skills / expertise */}
        <Section title="Skills / expertise" hint="Add your strengths — pick from suggestions or type your own." testid="skills-section">
          <TagSelect label="" values={c.skills || []} onChange={setVal("skills")} options={SKILLS} testid="cand-skills" />
        </Section>

        {/* Industries & functions */}
        <div className="border border-black/10 bg-white p-6 grid sm:grid-cols-2 gap-4" data-testid="industries-functions">
          <div className="space-y-2"><Label>Industries (comma sep)</Label><Input value={(c.industries || []).join(", ")} onChange={setList("industries")} className={inputCls} placeholder="fintech, ai" /></div>
          <div className="space-y-2"><Label>Functions (comma sep)</Label><Input value={(c.functions || []).join(", ")} onChange={setList("functions")} className={inputCls} placeholder="engineering, product" /></div>
        </div>

        {/* Preferred company stage & type */}
        <Section title="Preferred company stage" hint="Where you do your best work." testid="company-stage-section">
          <TagSelect label="" values={c.preferred_company_stages || []} onChange={setVal("preferred_company_stages")} options={COMPANY_STAGES} allowCustom={false} testid="cand-company-stages" />
        </Section>
        <Section title="Preferred company type" testid="company-type-section">
          <TagSelect label="" values={c.preferred_company_types || []} onChange={setVal("preferred_company_types")} options={COMPANY_TYPES} allowCustom={false} testid="cand-company-types" />
        </Section>

        {/* Notable achievements */}
        <Section title="Notable achievements" hint="Signals that make you stand out. Pick suggestions or add your own." testid="achievements-section">
          <TagSelect label="" values={c.notable_achievements || []} onChange={setVal("notable_achievements")} options={NOTABLE_ACHIEVEMENTS} testid="cand-achievements" />
        </Section>

        {/* Board experience */}
        <Section title="Board experience" testid="board-experience-section">
          <label className="flex items-center gap-2 text-sm mb-4"><Switch checked={!!c.has_board_experience} onCheckedChange={(v) => setC({ ...c, has_board_experience: v })} data-testid="cand-has-board" /> I have served on a board</label>
          {c.has_board_experience && (
            <TagSelect label="Board types" values={c.board_experience_types || []} onChange={setVal("board_experience_types")} options={BOARD_TYPES} allowCustom={false} testid="cand-board-types" />
          )}
        </Section>

        {/* Languages */}
        <Section title="Languages" hint="Languages you speak professionally." testid="languages-section">
          <TagSelect label="" values={c.languages || []} onChange={setVal("languages")} options={LANGUAGES} testid="cand-languages" />
        </Section>

        {/* Preferred role types */}
        <div className="border border-black/10 bg-white p-6" data-testid="preferred-roles">
          <p className="overline mb-1">Preferred role categories</p>
          <p className="text-xs text-muted-foreground mb-4">Which of our practice areas fit you? Powers your job alerts.</p>
          <div className="grid grid-cols-3 gap-px bg-black/10 border border-black/10">
            {CATEGORIES.map((cat) => {
              const on = (c.preferred_role_types || []).includes(cat);
              return (
                <button type="button" key={cat} data-testid={`pref-${cat}`} onClick={() => toggleRole(cat)}
                  className={`p-3 text-sm capitalize text-left transition-colors ${on ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>{cat}</button>
              );
            })}
          </div>
        </div>

        {/* Work experience */}
        <div className="border border-black/10 bg-white p-6" data-testid="work-experience">
          <div className="flex items-center justify-between mb-4">
            <div><p className="overline">Work experience</p><p className="text-xs text-muted-foreground mt-1">Your recent roles — what recruiters screen on first.</p></div>
            <Button type="button" variant="ghost" onClick={addExp} data-testid="add-exp" className="rounded-none border border-black/10 text-xs"><Plus className="h-3.5 w-3.5 mr-1" /> Add role</Button>
          </div>
          {exp.length === 0 && <p className="text-sm text-muted-foreground">No roles added yet.</p>}
          <div className="space-y-4">
            {exp.map((r, i) => (
              <div key={i} className="border border-black/10 p-4 relative" data-testid={`exp-${i}`}>
                <button type="button" onClick={() => removeExp(i)} data-testid={`remove-exp-${i}`} className="absolute top-3 right-3 text-muted-foreground hover:text-[#09090B]"><X className="h-4 w-4" /></button>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Company</Label><Input value={r.company || ""} onChange={(e) => setExp(i, "company", e.target.value)} className={inputCls} data-testid={`exp-company-${i}`} /></div>
                  <div className="space-y-2"><Label>Title</Label><Input value={r.title || ""} onChange={(e) => setExp(i, "title", e.target.value)} className={inputCls} data-testid={`exp-title-${i}`} /></div>
                  <div className="space-y-2"><Label>Start</Label><Input value={r.start || ""} onChange={(e) => setExp(i, "start", e.target.value)} placeholder="2020" className={inputCls} /></div>
                  <div className="space-y-2">
                    <Label>End</Label>
                    <Input value={r.current ? "" : (r.end || "")} disabled={r.current} onChange={(e) => setExp(i, "end", e.target.value)} placeholder={r.current ? "Present" : "2023"} className={inputCls} />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm mt-3"><Switch checked={!!r.current} onCheckedChange={(v) => setExp(i, "current", v)} /> I currently work here</label>
                <div className="space-y-2 mt-3"><Label>Impact / highlight</Label><Textarea value={r.impact || ""} onChange={(e) => setExp(i, "impact", e.target.value)} placeholder="e.g. Scaled ARR from $3M to $40M in 24 months" className={`${inputCls} min-h-[60px]`} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* Education */}
        <div className="border border-black/10 bg-white p-6" data-testid="education">
          <div className="flex items-center justify-between mb-4">
            <div><p className="overline">Education</p><p className="text-xs text-muted-foreground mt-1">Schools and degrees.</p></div>
            <Button type="button" variant="ghost" onClick={addEdu} data-testid="add-edu" className="rounded-none border border-black/10 text-xs"><Plus className="h-3.5 w-3.5 mr-1" /> Add education</Button>
          </div>
          {edu.length === 0 && <p className="text-sm text-muted-foreground">No education added yet.</p>}
          <div className="space-y-4">
            {edu.map((r, i) => (
              <div key={i} className="border border-black/10 p-4 relative" data-testid={`edu-${i}`}>
                <button type="button" onClick={() => removeEdu(i)} data-testid={`remove-edu-${i}`} className="absolute top-3 right-3 text-muted-foreground hover:text-[#09090B]"><X className="h-4 w-4" /></button>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="space-y-2 sm:col-span-1"><Label>School</Label><Input value={r.school || ""} onChange={(e) => setEdu(i, "school", e.target.value)} className={inputCls} data-testid={`edu-school-${i}`} /></div>
                  <div className="space-y-2 sm:col-span-1"><Label>Degree</Label><Input value={r.degree || ""} onChange={(e) => setEdu(i, "degree", e.target.value)} className={inputCls} data-testid={`edu-degree-${i}`} /></div>
                  <div className="space-y-2 sm:col-span-1"><Label>Grad year <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={r.year || ""} onChange={(e) => setEdu(i, "year", e.target.value)} placeholder="2015" className={inputCls} data-testid={`edu-year-${i}`} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Interests */}
        <div className="border border-black/10 bg-white p-6">
          <p className="overline mb-4">Board / fractional / advisory interest</p>
          <div className="flex flex-wrap gap-8">
            {[["board_interest", "Board seats"], ["fractional_interest", "Fractional roles"], ["advisor_interest", "Advisory"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={!!c[k]} onCheckedChange={(v) => setC({ ...c, [k]: v })} /> {l}</label>
            ))}
          </div>
        </div>

        {/* Links */}
        <div className="border border-black/10 bg-white p-6 grid sm:grid-cols-2 gap-4" data-testid="links-section">
          <div className="space-y-2 sm:col-span-2"><Label>Portfolio / personal website <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={c.portfolio_url || ""} onChange={set("portfolio_url")} className={inputCls} placeholder="https://…" data-testid="cand-portfolio" /></div>
          {SOCIAL_LINKS.map((s) => (
            <div key={s.key} className="space-y-2"><Label>{s.label}</Label><Input value={social[s.key] || ""} onChange={(e) => setSocial(s.key, e.target.value)} className={inputCls} placeholder={s.placeholder} data-testid={`cand-social-${s.key}`} /></div>
          ))}
        </div>

        {/* Resume */}
        <div className="border border-black/10 bg-white p-6">
          <p className="overline mb-1">Resume</p>
          {c.resume_file_url ? (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${c.resume_file_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline text-sm"><FileText className="h-4 w-4" /> {c.resume_filename || "View resume"}</a>
          ) : <p className="text-sm text-muted-foreground">No resume uploaded — use the Resume button above.</p>}
        </div>
      </form>
    </div>
  );
}
